import { AccountView } from "@bitwarden/web-vault/app/models/view/account/account.view";
import { AccountStoreModel } from "@bitwarden/web-vault/app/models/store/account.store.model";
import { Origin } from "@bitwarden/web-vault/app/models/types/general-types";
import { InstitutionView } from "@bitwarden/web-vault/app/models/view/institution/institution.view";
import { InstitutionAccountView } from "@bitwarden/web-vault/app/models/view/institution/institution-account.view";
import {
  formatAustralianSuperAccountDisplayName,
  tagAustralianSuperAccountModel,
} from "@bitwarden/web-vault/app/services/store/data-plugin/australian-super-account";
import { AUSTRALIAN_SUPER_PLUGIN_NAME } from "@bitwarden/web-vault/app/services/store/data-plugin/australian-super.plugin-config";
import { disambiguateAccountNames } from "./plugin-account-name.util";

export type PluginAccount = { balance: number; id: string; name: string; currency: "AUD" };

export type PluginAccountResponse = {
  account: PluginAccount[];
};

/**
 * Whether a raw plugin account is a real, nameable card rather than an unnamed placeholder — Opal,
 * at least, returns entries with an id but `name: null` and `balance: 0` alongside real cards, and
 * some plugins return a literal "Unavailable Account Name". Checked on the raw name, before
 * disambiguateAccountNames suffixes it: two placeholders in one fetch would otherwise become
 * "Unavailable Account Name (1)"/"(2)" and slip past an exact-match check.
 * Callers filter these out before counting duplicates/new accounts, so a "1 already added, 3 new"
 * notice isn't inflated by placeholders the user never asked to add.
 *
 * Australian Super is exempt: formatAustralianSuperAccountDisplayName always falls back to a
 * non-empty name, so a Super account is never actually "unnamed".
 */
export function isNamedPluginAccount(pluginAccount: PluginAccount, pluginName?: string): boolean {
  const name = pluginAccount.name?.trim().toLowerCase();
  return (
    pluginName === AUSTRALIAN_SUPER_PLUGIN_NAME || (!!name && name !== "unavailable account name")
  );
}

export function filterNamedPluginAccounts(
  fetchedAccounts: PluginAccount[],
  pluginName: string | undefined,
): PluginAccount[] {
  return fetchedAccounts.filter((account) => isNamedPluginAccount(account, pluginName));
}

export class PluginAccountsMap {
  static toAccountView(
    response: PluginAccountResponse,
    institution: InstitutionView,
    product: InstitutionAccountView,
    pluginName?: string,
    // Raw names of this plugin's accounts already in the vault (existingPluginAccountNames) —
    // passed in so disambiguateAccountNames can tell "Anthea" apart from a card added earlier
    // under the same name (e.g. two Opal cards for the same person), which a plugin fetch has no
    // label for.
    existingAccountNames: string[] = [],
    // Saved name per account id, for a resync: an account already in the vault keeps its name
    // while the plugin still reports the same base name, instead of being renumbered.
    savedNames: ReadonlyMap<string, string> = new Map(),
  ): AccountView[] {
    // Sorted by id (stable across fetches, unlike whatever order the plugin API happens to
    // return) so two identically-named accounts (e.g. two Opal cards for one person) always land
    // on the same "(1)"/"(2)" suffix on every resync, instead of swapping display names whenever
    // the API returns them in a different order.
    const orderedAccounts = [...response.account].sort((a, b) => a.id.localeCompare(b.id));
    const proposedNames = orderedAccounts.map((pluginAccount) =>
      pluginName === AUSTRALIAN_SUPER_PLUGIN_NAME
        ? formatAustralianSuperAccountDisplayName(pluginAccount.name)
        : pluginAccount.name,
    );
    const finalNames = disambiguateAccountNames(
      proposedNames,
      existingAccountNames,
      orderedAccounts.map((pluginAccount) => savedNames.get(pluginAccount.id)),
    );
    const now = new Date().toISOString();

    const accounts: AccountView[] = orderedAccounts.map((pluginAccount: PluginAccount, index) => {
      let accountView: AccountStoreModel = {
        dc: now,
        dm: now,
        id: pluginAccount.id,
        v: 3,
        rtInstLnk: {
          institutionId: institution.id,
          institutionAccountId: product.id,
        },
        vid: "",
        instLnk: {
          institutionId: institution.id,
          institutionAccountId: product.id,
        },
        curr: pluginAccount.currency,
        // Normalize null to "" — AccountStoreModel promises a string. Callers drop unnamed
        // placeholder cards beforehand (filterNamedPluginAccounts).
        name: finalNames[index] ?? "",
        crLm: 0,
        bal: pluginAccount.balance,
        tz: "Australia/Sydney",
        dfltCla: [],
        dfltCat: [],
        ori: null,
        bsqId: null,
        date: now,
        type: "other",
        st: [],
      };
      if (pluginName === AUSTRALIAN_SUPER_PLUGIN_NAME) {
        accountView = tagAustralianSuperAccountModel(accountView);
      } else if (pluginName) {
        // Tag every other plugin account with its plugin id so account-details can find the
        // right plugin to re-fetch from (see getPluginIdForAccount).
        accountView = { ...accountView, ori: Origin.plugin, bsqId: pluginName };
      }
      return new AccountView(accountView);
    });
    return accounts;
  }
}
